import { useCallback, useEffect, useState } from 'react';
import { useAddress, useContract } from '@thirdweb-dev/react';
import { useAsync, useAsyncFn } from 'react-use';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { useConfig } from '@app/config';

export function useStake() {
  const config = useConfig();
  const address = useAddress();
  const [state, setState] = useState<{
    amount: string;
  }>({
    amount: ''
  });

  const handleAmountChange = useCallback((val: string) => {
    setState(prev => ({
      ...prev,
      amount: val
    }));
  }, []);

  const { contract: kimContract } = useContract(config?.CONTRACTS.KIM);
  const { contract: xKimContract } = useContract(config?.CONTRACTS.X_KIM);

  const { value, loading: loadingTokenBalance } = useAsync(async () => {
    if (!kimContract || !xKimContract || !address) {
      return;
    }

    const kimTokenBalance = await kimContract.call('balanceOf', [address]);
    const xKimTokenBalance = await xKimContract.call('balanceOf', [address]);

    return {
      myRyoBalance: formatUnits(kimTokenBalance, 18),
      myXRyoBalance: formatUnits(xKimTokenBalance, 18)
    };
  }, [kimContract, address]);

  const [{ loading: allocating }, allocate] = useAsyncFn(async () => {
    if (!xKimContract || !address) {
      return;
    }

    return xKimContract?.call('allocate', [
      config?.CONTRACTS.NFT_MARKETPLACE,
      parseUnits(state.amount, 18),
      []
    ]);
  }, [xKimContract, address, state.amount, config]);

  const resetStateValues = useCallback(() => {
    setState(prev => ({
      amount: ''
    }));
  }, []);

  const myXRyoBalanceValue = !value?.myXRyoBalance ? 0 : value?.myXRyoBalance;

  return {
    state,
    handleAmountChange,
    myXRyoBalance: myXRyoBalanceValue,
    allocate,
    allocating,
    loadingTokenBalance,
    resetStateValues
  };
}

export function useXKimTokenApprove(amount: string) {
  const address = useAddress();
  const config = useConfig();

  const { contract } = useContract(config?.CONTRACTS.X_KIM);

  const [{ value, loading: isFetchingAllowance }, getAllowance] =
    useAsyncFn(async () => {
      if (!contract || !address) {
        return;
      }

      try {
        const res = await contract.call('usageApprovals', [
          address,
          config?.CONTRACTS.NFT_MARKETPLACE
        ]);

        return formatUnits(res, 18);
      } catch (e) {
        // do nothing
      }
    }, [contract, address, config, amount]);

  const [{ loading }, approve] = useAsyncFn(async () => {
    if (value === undefined || Number(amount) <= Number(value) || !contract) {
      return;
    }

    return contract?.call('approveUsage', [
      config?.CONTRACTS.NFT_MARKETPLACE ?? '',
      parseUnits(amount, 18)
    ]);
  }, [contract, amount, value, config]);

  useEffect(() => {
    getAllowance();
  }, [getAllowance]);

  return {
    requiresApproval:
      !isFetchingAllowance &&
      value !== undefined &&
      Number(amount) > Number(value),
    approve,
    isApproveInProgress: loading,
    getAllowance
  };
}
